"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FieldsContext,
  HandleBlocklyFieldsProps,
  RulesContextMethod,
  RulesFormMethod,
} from "./lib/types";
import { runBlocklyScript } from "./lib/utils";
import { SerializedNode, useEditor } from "@craftjs/core";
import { useFormContext, useWatch } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import { safeParce } from "../useWebPageComponent";
import { RuleModel, RuleType } from "@/src/shared/models/rule";
import { useContextStore } from "@/src/stores/useContextStore";
import { isObject } from "lodash";
import { useSearchParams } from "next/navigation";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { parseContextVariableValue } from "@/src/shared/utils/dataTypeHelpers";
import { updateProcessInstanceField } from "@/src/api/services/processInstance";
import { create, createStore, useStore } from "zustand";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import {parseIdentCode, transliterateFromCyrillic} from "@/src/shared/utils/string";
import { dateDiff } from "@/src/shared/utils/dateTime";
import {decode_ipn_rule, decode_ipn_rule_simple} from "@/src/hooks/blockly/const";

// FIXME: remove this when we have a migration for rules on single function (admin panel)
const checkAndReplaceCode = (rule: RuleModel): RuleModel => {
  const tmpCodeStr = rule.code.slice().replaceAll(" ", "").replaceAll("\n", "");
  if (tmpCodeStr === decode_ipn_rule) {
    return {
        ...rule,
        code: decode_ipn_rule_simple,
    }
  }
  return rule;
}

const getEditorData = (serializedQuery: string) => {
  const parsedQuery = safeParce(serializedQuery);

  if (typeof parsedQuery === "string" || !Object.keys(parsedQuery.ids).length) {
    return null;
  }

  return parsedQuery.ids;
};

export const actionRunningStore = createStore<{
  isActionRunning: boolean;
  isRunned: boolean;
  setIsActionRunning: (value: boolean) => void;
  setIsRunned: (value: boolean) => void;
}>((set) => ({
  isActionRunning: false,
  isRunned: false,
  setIsRunned: (value: boolean) => set({ isRunned: value }),
  setIsActionRunning: (value: boolean) => set({ isActionRunning: value }),
}));

export const useUpdateField = () => {
  const searchParams = useSearchParams();
  const processInstanceId = useProcessInstanceStore(
    (state) => state.processInstance.id,
  );
  const getContextVariable = useContextStore((state) => state.getVariable);

  const updateField = useCallback(
    (field: string, value: any, allowNull = false) => {
      const variable = getContextVariable(field);
      const variableId = variable?.id;

      const currentValue = parseContextVariableValue(value);
      console.log(
        "TEST UPDATE FROM RULE",
        allowNull,
        currentValue,
        processInstanceId,
        variableId,
      );
      if ((!allowNull && !currentValue) || !processInstanceId || !variableId)
        return;

      try {
        updateProcessInstanceField({
          processInstanceId,
          variableId,
          value: currentValue as any,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [processInstanceId],
  );
  const updateFieldAsync = useCallback(
    async (field: string, value: any, allowNull = false) => {
      const variable = getContextVariable(field);
      const variableId = variable?.id;

      const currentValue = parseContextVariableValue(value);
      if ((!allowNull && !currentValue) || !processInstanceId || !variableId)
        return;

      try {
        await updateProcessInstanceField({
          processInstanceId,
          variableId,
          value: currentValue as any,
        });
        console.log("AWAIT FINISHED");
      } catch (e) {
        console.error(e);
      }
    },
    [processInstanceId],
  );

  return { updateField, updateFieldAsync };
};

export const submitStore = create<{
  beforeSubmitActions: Record<
    string,
    (formValues: any) => void | Promise<void>
  >;
}>(() => ({
  beforeSubmitActions: {},
}));

export const runBeforeSubmitActions = async (formValues: any) => {
  await Promise.all(
    Object.entries(submitStore.getState().beforeSubmitActions).map(
      ([, action]) => {
        return action(formValues);
      },
    ),
  );
};

export const useHandleBlocklyFields = ({
  fields,
  rules,
}: HandleBlocklyFieldsProps) => {
  const {
    query,
    actions: { setHidden, setProp },
  } = useEditor();
  const {
    control,
    formState,
    setValue,
    setError,
    clearErrors,
    getValues,
    getFieldState,
    watch,
  } = useFormContext();
  const { variables } = useContextStore();
  // updates field value in BE context
  const { updateField: updateFormContextField } = useUpdateField();
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);

  const hookFormFields = useWatch({ control });

  const [formFields, setFormFields] = useState<[string, SerializedNode][]>(
    Object.entries(fields),
  );

  const formRulesObject = useMemo(() => {
    return rules.reduce(
      (sum, curr) => {
        const currentType = curr.type || RuleType.RUN_ON_FORM_CHANGE;

        const currentTypeRules = sum[currentType] || [];

        const updatedTypeRules = [...currentTypeRules, curr];

        const updatedRulesObject = {
          ...sum,
          [currentType]: updatedTypeRules,
        };

        return { ...updatedRulesObject };
      },
      {
        [RuleType.RUN_ON_FORM_CHANGE]: [],
        [RuleType.RUN_ON_MOUNT]: [],
        [RuleType.RUN_ON_SUBMIT]: [],
      },
    );
  }, [rules]);

  const formRules = useMemo(
    () => formRulesObject[RuleType.RUN_ON_FORM_CHANGE],
    [formRulesObject],
  );
  const mountRules = useMemo(
    () => formRulesObject[RuleType.RUN_ON_MOUNT],
    [formRulesObject],
  );
  const submitRules = useMemo(
    () => formRulesObject[RuleType.RUN_ON_SUBMIT],
    [formRulesObject],
  );

  useEffect(() => {
    if (!formFields.length && fields.length) {
      setFormFields(Object.entries(fields));
    }
  }, [fields]);

  const fieldsContext = useMemo<FieldsContext>(() => {
    const updateField = (
      fieldName: string,
      key: string,
      value: string | number | boolean,
    ) => {
      const findElId = formFields.find(
        ([, value]) => value.props.elementName === fieldName,
      )?.[0];

      if (!findElId) {
        console.error("updateField not found", fieldName, key, value);
        return;
      }

      if (key === "hidden") {
        setHidden(findElId, !!value);
        setProp(findElId, (props) => {
          props["hidden"] = value;
        });
      }
      if (key === "value") {
        setProp(findElId, (props) => {
          const fieldName = props["name"];
          props[key] = value;
          if (
            typeof value === "object"
              ? JSON.stringify(value) !==
                JSON.stringify(hookFormFields[fieldName])
              : hookFormFields[fieldName] !== value
          ) {
            setValue(fieldName, value);
          }
        });
        return;
      }
      if (key === "checked") {
        setProp(findElId, (props) => {
          const fieldName = props["name"];
          props[key] = value;
          if (hookFormFields[fieldName] !== value) {
            setValue(fieldName, value);
          }
        });
        return;
      }

      if (key === "options" && typeof value === "string") {
        setProp(findElId, (props) => {
          const excludedOptions = value.split("; ");
          props["excludedOptions"] = excludedOptions;
        });

        return;
      }

      if (key === "validationMessage") {
        const fieldName = query.node(findElId).get()?.data?.props?.name;
        if (value) {
          setError(fieldName, {
            message: value as string,
          });
        } else {
          clearErrors(fieldName);
        }
        // setProp(findElId, (props) => {
        //     const fieldName = props["name"];
        // });
        return;
      }

      if (key === "minLength" || key === "maxLength") {
        setError(fieldName, {
          type: "custom",
          message: value as string,
        });
        clearErrors(fieldName);
      }

      setProp(findElId, (props) => {
        props[key] = value;
      });
    };

    const updateContextField: RulesContextMethod = (fieldName, value) => {
      const currentFieldValue = useContextStore
        .getState()
        .getVariable(fieldName)?.value;

      if (
        typeof value === "object"
          ? JSON.stringify(value) === JSON.stringify(currentFieldValue)
          : currentFieldValue === value
      ) {
        return;
      }
      useContextStore.getState().updateVariableValue(fieldName, value);
    };
    const updateFormField: RulesFormMethod = (fieldName, value) => {
      console.log(fieldName, value);
      const currentFieldValue = getValues(fieldName);
      if (
        typeof value === "object"
          ? JSON.stringify(value) === JSON.stringify(currentFieldValue)
          : currentFieldValue === value
      ) {
        return;
      }
      setValue(fieldName, value, { shouldDirty: true });
      updateFormContextField(fieldName, value);
    };

    return {
      fieldValues: hookFormFields ? { ...hookFormFields } : {},
      fieldMethods: {
        label: (fieldName, payload) => updateField(fieldName, "label", payload),
        hidden: (fieldName, payload) =>
          updateField(fieldName, "hidden", payload),
        disabled: (fieldName, payload) =>
          updateField(fieldName, "disabled", payload),
        required: (fieldName, payload) =>
          updateField(fieldName, "required", payload),
        defaultValue: (fieldName, payload) =>
          updateField(fieldName, "defaultValue", payload),
        value: (fieldName, payload) => updateField(fieldName, "value", payload),
        validationMessage: (fieldName, payload) =>
          updateField(fieldName, "validationMessage", payload),
        placeholder: (fieldName, payload) =>
          updateField(fieldName, "placeholder", payload),
        checked: (fieldName, payload) =>
          updateField(fieldName, "checked", payload),
        options: (fieldName, payload) =>
          updateField(fieldName, "options", payload),
        open: (fieldName, payload) => updateField(fieldName, "open", payload),
        minLength: (fieldName, payload) =>
          updateField(fieldName, "minLength", payload),
        maxLength: (fieldName, payload) =>
          updateField(fieldName, "maxLength", payload),

        minDaysShift: (fieldName, payload) =>
          updateField(fieldName, "minDaysShift", payload),
        maxDaysShift: (fieldName, payload) =>
          updateField(fieldName, "maxDaysShift", payload),
        minYearsShift: (fieldName, payload) =>
          updateField(fieldName, "minYearsShift", payload),
        maxYearsShift: (fieldName, payload) =>
          updateField(fieldName, "maxYearsShift", payload),

        pattern: (fieldName, payload) =>
          updateField(fieldName, "pattern", payload),
        patternErrorMessage: (fieldName, payload) =>
          updateField(fieldName, "patternErrorMessage", payload),
      },

      contextMethods: {
        setValue: (fieldName, value) => updateContextField(fieldName, value),
      },

      formMethods: {
        setValue: (fieldName, value) => updateFormField(fieldName, value),
        isFieldDirty: (fieldName) => !!getFieldState(fieldName).isDirty,
        isFormDirty: () => Object.keys(formState.dirtyFields).length > 0,
      },
      dateUtils: {
        substract: (date2, date1) => {
          const timeDiff = dateDiff(date1, date2, "days");
          return timeDiff;
        },
      },
      stringUtils: {
        transliterateFromCyrillic: transliterateFromCyrillic,
        decodeIpn: (ipn: string) => {
          const value = parseIdentCode(ipn);
          updateFormField("birthDate", value)
        }
      },
    };
  }, [hookFormFields, formFields, variables, processInstanceId]);

  const getFieldsContext = useCallback(
    (hookFormFields: any) => {
      return ((): FieldsContext => {
        const updateField = (
          fieldName: string,
          key: string,
          value: string | number | boolean,
        ) => {
          const findElId = formFields.find(
            ([, value]) => value.props.elementName === fieldName,
          )?.[0];

          if (!findElId) {
            console.error("updateField not found");
            return;
          }

          if (key === "hidden") {
            setHidden(findElId, !!value);
            setProp(findElId, (props) => {
              props["hidden"] = value;
            });
          }
          if (key === "value") {
            setProp(findElId, (props) => {
              const fieldName = props["name"];
              props[key] = value;
              if (
                typeof value === "object"
                  ? JSON.stringify(value) !==
                    JSON.stringify(hookFormFields[fieldName])
                  : hookFormFields[fieldName] !== value
              ) {
                setValue(fieldName, value);
              }
            });
            return;
          }
          if (key === "checked") {
            setProp(findElId, (props) => {
              const fieldName = props["name"];
              props[key] = value;
              if (hookFormFields[fieldName] !== value) {
                setValue(fieldName, value);
              }
            });
            return;
          }

          if (key === "options" && typeof value === "string") {
            setProp(findElId, (props) => {
              const excludedOptions = value.split("; ");
              props["excludedOptions"] = excludedOptions;
            });

            return;
          }
          if (key === "validationMessage") {
            const fieldName = query.node(findElId).get()?.data?.props?.name;
            if (value) {
              setError(fieldName, {
                message: value as string,
              });
            } else {
              clearErrors(fieldName);
            }
            // setProp(findElId, (props) => {
            //     const fieldName = props["name"];
            // });
            return;
          }

          if (key === "minLength" || key === "maxLength") {
            setError(fieldName, { type: "custom", message: value as string });
            clearErrors(fieldName);
          }

          setProp(findElId, (props) => {
            props[key] = value;
          });
        };

        const updateContextField: RulesContextMethod = (fieldName, value) => {
          const currentFieldValue = useContextStore
            .getState()
            .getVariable(fieldName)?.value;

          if (
            isObject(value)
              ? JSON.stringify(value) === JSON.stringify(currentFieldValue)
              : currentFieldValue === value
          ) {
            return;
          }

          useContextStore.getState().updateVariableValue(fieldName, value);
        };
        const updateFormField: RulesFormMethod = (fieldName, value) => {
          const currentFieldValue = getValues(fieldName);
          if (
            typeof value === "object"
              ? JSON.stringify(value) === JSON.stringify(currentFieldValue)
              : currentFieldValue === value
          ) {
            return;
          }
          console.log("updateFormField", fieldName, value, currentFieldValue);
          setValue(fieldName, value);
          updateFormContextField(fieldName, value, true);
        };

        return {
          fieldValues: hookFormFields ? { ...hookFormFields } : {},
          fieldMethods: {
            label: (fieldName, payload) =>
              updateField(fieldName, "label", payload),
            hidden: (fieldName, payload) =>
              updateField(fieldName, "hidden", payload),
            disabled: (fieldName, payload) =>
              updateField(fieldName, "disabled", payload),
            required: (fieldName, payload) =>
              updateField(fieldName, "required", payload),
            defaultValue: (fieldName, payload) =>
              updateField(fieldName, "defaultValue", payload),
            value: (fieldName, payload) =>
              updateField(fieldName, "value", payload),
            validationMessage: (fieldName, payload) =>
              updateField(fieldName, "validationMessage", payload),
            placeholder: (fieldName, payload) =>
              updateField(fieldName, "placeholder", payload),
            checked: (fieldName, payload) =>
              updateField(fieldName, "checked", payload),
            options: (fieldName, payload) =>
              updateField(fieldName, "options", payload),
            open: (fieldName, payload) =>
              updateField(fieldName, "open", payload),
            minLength: (fieldName, payload) =>
              updateField(fieldName, "minLength", payload),
            maxLength: (fieldName, payload) =>
              updateField(fieldName, "maxLength", payload),

            minDaysShift: (fieldName, payload) =>
              updateField(fieldName, "minDaysShift", payload),
            maxDaysShift: (fieldName, payload) =>
              updateField(fieldName, "maxDaysShift", payload),
            minYearsShift: (fieldName, payload) =>
              updateField(fieldName, "minYearsShift", payload),
            maxYearsShift: (fieldName, payload) =>
              updateField(fieldName, "maxYearsShift", payload),

            pattern: (fieldName, payload) =>
              updateField(fieldName, "pattern", payload),
            patternErrorMessage: (fieldName, payload) =>
              updateField(fieldName, "patternErrorMessage", payload),
          },

          contextMethods: {
            setValue: (fieldName, value) =>
              updateContextField(fieldName, value),
          },

          formMethods: {
            setValue: (fieldName, value) => updateFormField(fieldName, value),
            isFieldDirty: (fieldName) => !!getFieldState(fieldName).isDirty,
            isFormDirty: () => Object.keys(formState.dirtyFields).length > 0,
          },

          dateUtils: {
            substract: (date2, date1) => {
              const timeDiff = dateDiff(date1, date2, "days");
              return timeDiff;
            },
          },
          stringUtils: {
            transliterateFromCyrillic: transliterateFromCyrillic,
            decodeIpn: (ipn: string) => {
              const value = parseIdentCode(ipn);
              updateFormField("birthDate", value)
            }
          },
        };
      })();
    },
    [formFields, variables],
  );

  useEffect(() => {
    const currentEditorState = getEditorData(query.serialize());
    if (currentEditorState) {
      setFormFields(
        Object.entries(currentEditorState) as unknown as [
          string,
          SerializedNode,
        ][],
      );
    }
  }, []);

  const debouncedRunRules = useDebouncedCallback((formRules, fieldsContext) => {
    formRules.forEach((rule: RuleModel) => {
      try {
        if (rule.runOn?.length) return;
        runBlocklyScript([checkAndReplaceCode(rule)], fieldsContext);
      } catch (error) {
        console.log("runBlocklyScript", [error], rule);
      }
    });
  }, 200);

  const runDependendRules = useCallback(
    (formRules: RuleModel[], fieldsContext: any, fieldName: string) => {
      const rulesForRun = formRules.filter(({ runOn }) =>
        runOn?.includes(fieldName),
      );

      //console.log("runDependendRules", rulesForRun, fieldName, formRules);
      rulesForRun.forEach((rule: RuleModel) => {
        try {
          runBlocklyScript([checkAndReplaceCode(rule)], fieldsContext);
        } catch (error) {
          console.log("runBlocklyScript", [error], rule);
        }
      });
    },
    [],
  );

  const runRules = (formRules: RuleModel[], fieldsContext: FieldsContext) => {
    formRules.forEach((rule) => {
      try {
        runBlocklyScript([checkAndReplaceCode(rule)], fieldsContext);
      } catch (error) {
        console.log("runBlocklyScript", [error], rule);
      }
    });
  };
  // const [isRunned, setIsRunned] = useState(false);
  const isActionRunning = useStore(
    actionRunningStore,
    ({ isActionRunning }) => isActionRunning,
  );
  const isRunned = useStore(actionRunningStore, ({ isRunned }) => isRunned);
  const setIsRunned = useStore(
    actionRunningStore,
    ({ setIsRunned }) => setIsRunned,
  );

  useEffect(() => {
    if (!isActionRunning) {
      setIsRunned(false);
    }
  }, [mountRules, isActionRunning]);

  useEffect(() => {
    console.log("ISRUNNED", isRunned);
    if (
      isRunned ||
      formState.isSubmitting ||
      formState.isDirty ||
      !Object.keys(hookFormFields).length ||
      !processInstanceId
    ) {
      return;
    }
    console.log(
      "mountRules are running",
      mountRules,
      formState.isSubmitting,
      formState.isDirty,
      control._formState.isDirty,
      control._formState.isValidating,
    );
    runRules(mountRules, fieldsContext);
    setIsRunned(true);
  }, [mountRules, hookFormFields, processInstanceId, isRunned]);

  const runOnSubmitRules = useCallback(
    (value: any) => {
      console.log("submit rules are running", submitRules);
      runRules(submitRules, getFieldsContext(value));
    },
    [submitRules],
  );

  useEffect(() => {
    submitStore.setState(({ beforeSubmitActions }) => ({
      beforeSubmitActions: {
        ...beforeSubmitActions,
        runSubmitRules: runOnSubmitRules,
      },
    }));
  }, [runOnSubmitRules]);

  useEffect(() => {
    console.log("debouncedRunRules rules are running", formRules);
    debouncedRunRules(formRules, fieldsContext);
  }, [fieldsContext, formRules]);

  useEffect(() => {
    const { unsubscribe } = watch((value, { name }) => {
      name && runDependendRules(formRules, getFieldsContext(value), name);
    });

    return unsubscribe;
  }, [
    formRules,
    runDependendRules,
    watch,
    getFieldsContext,
    updateFormContextField,
  ]);

  return { formFields: Object.fromEntries(formFields) };
};
